/* eslint-disable max-lines-per-function */
import classNames from 'clsx';
import { ReactComponent as EmptyAllDocsIcon } from 'img/icons/emptyAllDocs.svg';
import { ReactComponent as EmptyAttachIcon } from 'img/icons/emptyAttach.svg';
import { ReactComponent as EmptyBinIcon } from 'img/icons/emptyBin.svg';
import { ReactComponent as EmptyBinNewIcon } from 'img/icons/emptyBinNew.svg';
import { ReactComponent as LoopIcon } from 'img/icons/loop.svg';
import { ReactComponent as EmptyFolderIcon } from 'img/icons/uploadStroke.svg';
import React, { memo, ReactElement, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getDomainFoldersFilterActive } from 'reactApp/modules/home/home.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { setButtonTypeClickAction } from 'reactApp/modules/upload/upload.module';
import { useUploadInputHandlers } from 'reactApp/modules/uploading/hooks/useUploadInputHandlers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { LinkIcon } from 'reactApp/ui/VKUIIcons';
/* tempexp_14812-next-line */
import { sendEmptyStatesAnalitics } from 'reactApp/utils/emptyStatesGa';
import { sendGa } from 'reactApp/utils/ga';

import styles from './Empty.css';
/* tempexp_14812-next-line */
import { Empty as EmptyNew } from './new/Empty';

interface HomeProps {
    type: 'folder';
    storage: EStorageType;
    canUpload?: boolean;
    canCoshare?: boolean;
    weblink?: string;
    id?: string;
    currentAllDocsType?: EAllDocumentsType | null;
    isNewTrashbin?: boolean;
}

type Props =
    | HomeProps
    | {
          type: 'search';
          searchText?: string;
          searchType?: string;
          storage: EStorageType;
          id?: string;
          canUpload?: boolean;
          isNewTrashbin?: boolean;
      }
    | {
          type: 'gallery';
          searchType?: string;
          storage: EStorageType;
          id?: string;
          canUpload?: boolean;
          isNewTrashbin?: boolean;
      };

const getDocTypeName = (type?: EAllDocumentsType | null, isPlural = true) => {
    switch (type) {
        case EAllDocumentsType.document:
            return isPlural ? 'документов' : 'документ';
        case EAllDocumentsType.spreadsheet:
            return isPlural ? 'таблиц' : 'таблицу';
        case EAllDocumentsType.presentation:
            return isPlural ? 'презентаций' : 'презентацию';
        case EAllDocumentsType.pdf:
            return isPlural ? 'PDF документов' : 'PDF документ';
        default:
            return '';
    }
};

const getIcon = (props: Props) => {
    const { storage, type, isNewTrashbin, canUpload } = props;

    if (type === 'search' || storage === EStorageType.feed || storage === EStorageType.quotaCleaner) {
        return <LoopIcon />;
    }

    if (storage === EStorageType.trashbin) {
        return isNewTrashbin ? <EmptyBinNewIcon /> : <EmptyBinIcon />;
    } else if (storage === EStorageType.attaches) {
        return <EmptyAttachIcon />;
    } else if (storage === EStorageType.gallery) {
        return null;
    } else if (storage === EStorageType.alldocuments) {
        return <EmptyAllDocsIcon />;
    } else if (canUpload) {
        return <EmptyFolderIcon />;
    }
};

const getTitle = (props: Props) => {
    if (props.type === 'search') {
        const { searchText, searchType } = props;

        const type = mapTypeName[searchType || ''];

        if (searchText && type) {
            return (
                <span>
                    По запросу <span className={styles.highlight}>{searchText}</span> {type.toLowerCase()} не&nbsp;
                    {type === mapTypeName.audio ? 'найдена' : 'найдены'}
                </span>
            );
        } else if (type) {
            return (
                <span>
                    {type} не&nbsp;{type === mapTypeName.audio ? 'найдена' : 'найдены'}
                </span>
            );
        }
        return (
            <span>
                По запросу <span className={styles.highlight}>{searchText}</span> ничего не&nbsp;найдено
            </span>
        );
    } else if (props.type === 'gallery') {
        const { searchType } = props;

        return searchType === 'mobile' ? 'Сейчас у вас нет мобильных фотографий' : 'Сейчас у вас нет фотографий';
    }

    const { storage, currentAllDocsType, isNewTrashbin } = props;

    if (storage === EStorageType.trashbin) {
        return isNewTrashbin ? 'В корзине пусто' : 'Корзина пуста';
    } else if (storage === EStorageType.attaches) {
        return 'Сейчас у вас нет файлов из почты';
    } else if (storage === EStorageType.feed) {
        return 'По запросу ничего не найдено';
    } else if (storage === EStorageType.alldocuments) {
        return `У вас пока нет ${getDocTypeName(currentAllDocsType)}`;
    } else if (storage === EStorageType.sharedIncoming) {
        return (
            <>
                С вами пока ничем не поделились
                <div className={styles.text}>здесь появятся файлы и папки, которыми с вами поделятся</div>
            </>
        );
    }
    return 'Папка пока пуста';
};

const getText = (props: Props, onUploadClick, inputRef) => {
    if (props.type === 'search') {
        const { searchText, searchType } = props;
        const type = mapTypeName[searchType || ''];

        if (type && searchText) {
            return 'Проверьте правильность написания или выберите другой тип файлов';
        } else if (type) {
            return 'Попробуйте использовать поиск по названию  или выберите другой тип файлов';
        }
        return 'Проверьте правильность написания или используйте другой запрос';
    }

    const { storage, canUpload, isNewTrashbin } = props;

    if (storage === EStorageType.trashbin) {
        return isNewTrashbin
            ? 'Файлы хранятся в корзине 14 дней, а\u00A0затем\nавтоматически удаляются'
            : 'Вы всегда можете удалить файлы из корзины чтобы освободить место';
    } else if (storage === EStorageType.attaches) {
        return 'Все файлы из ваших почтовых переписок будут автоматически попадать сюда';
    } else if (storage === EStorageType.alldocuments) {
        return 'Они будут надёжно защищены и доступны\nна всех ваших устройствах';
    } else if (canUpload) {
        return (
            <>
                Перенесите файлы сюда или нажмите
                <br />
                на кнопку{' '}
                <ButtonLink onClick={onUploadClick} fontInherit primary>
                    Загрузить
                </ButtonLink>
                <input type="file" multiple className={styles.input} ref={inputRef} />
            </>
        );
    }
};

/* TODO: удалить эту и другие референсы к поиску после успешного эксперимента "new_search_UI" */
const mapTypeName = {
    image: 'Изображения',
    audio: 'Музыка',
    video: 'Видео',
    document: 'Документы',
    pdf: 'Файлы PDF',
    spreadsheet: 'Таблицы',
    presentation: 'Презентации',
    archive: 'Архивы',
    folder: 'Папки',
};

export const EmptyComponent = memo((props: Props): ReactElement => {
    const { storage = EStorageType.search, isNewTrashbin } = props;
    const { canCoshare, weblink, id = '', currentAllDocsType } = props as HomeProps;
    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const domainFoldersFilterActive = useSelector(getDomainFoldersFilterActive);

    /* tempexp_14812-start */
    useEffect(() => {
        sendEmptyStatesAnalitics({ action: 'show' });
    }, []);
    /* tempexp_14812-end */

    const canCreateDoc =
        storage === EStorageType.alldocuments &&
        (currentAllDocsType === EAllDocumentsType.document || currentAllDocsType === EAllDocumentsType.spreadsheet);

    const onUploadClick = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    }, [inputRef.current]);

    const handlePublishing = useCallback(() => {
        sendGa('empty-foldr', 'click', 'publish');
        publishHelper({ item: { id } });
    }, []);

    const handleCopy = useCallback(() => {}, []);

    const handleCreateDocs = useCallback(
        () => toolbarActions.create(currentAllDocsType === EAllDocumentsType.spreadsheet ? 'xlsx' : 'docx', 'empty', true, 'docs'),
        []
    );

    const { subscribeToInput, unsubscribeOfInput } = useUploadInputHandlers();

    useEffect(() => {
        subscribeToInput(inputRef.current);

        return () => {
            unsubscribeOfInput(inputRef.current);
        };
    }, ['canUpload' in props && props.canUpload]);

    useEffect(() => {
        sendGa('empty-foldr', 'show');
        dispatch(setButtonTypeClickAction('click'));
    }, [id]);

    return (
        <div className={classNames(styles.root, { [styles.rootNewBin]: isNewTrashbin })}>
            <div className={styles.file}>
                {storage !== EStorageType.alldocuments && !isNewTrashbin && (
                    <FileIcon type={storage === EStorageType.gallery ? 'image' : null} />
                )}
                <div className={styles.icon}>{getIcon(props)}</div>
            </div>
            <div className={styles.title}>{getTitle(props)}</div>
            <div className={styles.text}>{getText(props, onUploadClick, inputRef)}</div>
            {storage === EStorageType.home && canCoshare && !domainFoldersFilterActive && (
                <div className={styles.controls}>
                    {weblink ? (
                        <CopyWeblink publicUrl={weblink} onCopy={handleCopy} onClick={handlePublishing} theme="folder" />
                    ) : (
                        <>
                            <div className={styles.link}>
                                <LinkIcon />
                            </div>
                            <div className={styles.publish} onClick={handlePublishing}>
                                Поделиться папкой по ссылке
                            </div>
                        </>
                    )}
                </div>
            )}
            {canCreateDoc && (
                <div className={styles.controls}>
                    <Button theme="octavius" sizeMode={ButtonSizeMode.small} onClick={handleCreateDocs}>
                        Создать {getDocTypeName(currentAllDocsType, false)}
                    </Button>
                </div>
            )}
        </div>
    );
});

EmptyComponent.displayName = 'Empty';

/* tempexp_14812-next-line */
export const Empty = Object.assign(EmptyComponent, { New: EmptyNew });
